import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorService } from '@web/common';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AdminCosmeticDetailsFormComponent } from '../admin-cosmetic-details-form/admin-cosmetic-details-form.component';
import {
  AdminCosmeticDataSourceService,
  AdminCosmeticDetailsForm,
} from '@web/shared/module/cosmetic';

@Component({
  selector: 'app-admin-cosmetic-edit',
  imports: [
    AlertsComponent,
    CommonModule,
    SpinnerFilledComponent,
    AdminCosmeticDetailsFormComponent,
  ],
  templateUrl: './admin-cosmetic-edit.component.html',
  styleUrl: './admin-cosmetic-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminCosmeticEditComponent {
  private readonly cosmeticDataSource = inject(AdminCosmeticDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly id = input.required<Guid>();
  public readonly submitSuccessEvent = output<void>();

  protected readonly id$ = toObservable(this.id);
  protected readonly form$ = this.formSource();

  public handleSubmitClickEvent(data: AdminCosmeticDetailsForm): void {
    this.cosmeticDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => this.notifySubmitSuccessEvent(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private formSource(): Observable<AdminCosmeticDetailsForm> {
    return this.id$.pipe(
      switchMap((id) => this.cosmeticDataSource.fetchDetailsFormById(id)),
    );
  }
}
